import type { CardPartner } from 'api/swagger/definitions/backoffice'
import type { FraudCase, FraudPurchaseTransaction } from '../types'

export type NotificationTemplateParams = {
  fraudCase: FraudCase
  cardholderName: string
  cardLastFourDigits: string
}

const isPurchaseTransaction = (tx: FraudCase['transactions'][number]): tx is FraudPurchaseTransaction => tx.transactionType === 'PURCHASE'

const formatTransactionLines = (fraudCase: FraudCase) =>
  fraudCase.transactions
    .map((tx) => {
      const amountText = isPurchaseTransaction(tx) ? `Amount ${tx.amount.value} ${tx.amount.currency}` : 'Auth verification'
      return `${tx.transactionDate.slice(0, 10)}, TRANSAKTION: ${tx.description}: ${amountText}`
    })
    .join('\n')

const buildDefaultNotificationText = ({ fraudCase, cardholderName, cardLastFourDigits }: NotificationTemplateParams) => {
  const txLines = formatTransactionLines(fraudCase)

  return `Hej ${cardholderName},

Vi har observerat transaktionsförsök på ert företagskort som slutar på nummer ${cardLastFourDigits} och undrar om det är köp som du själv har genomfört? Ditt kort är blockerat i dagsläget tills att vi fått tag i dig.
De transaktioner det gäller är följande:

${txLines}

Anledningen till att vi kontaktar dig är för att säkerställa att ditt kort inte har förlorats och att ingen obehörig har fått tag på dina kortuppgifter. Om ni inte har utfört dessa köp, vänligen besvara följande frågor:

· Har ni tidigare använt ert kort hos denna handlare?
· Har ni nyligen angett era kortuppgifter på någon okänd webbplats eller tjänst?

Om det är så att du har genomfört dessa köp, informera oss om vad de gäller och hur de relaterar till er bolagsbeskrivning. Därefter kan vi häva den tillfälliga blockeringen på kortet.
Om olovliga transaktioner har gjorts på kortet och behöver bestridas, är det viktigt att detta sker inom 120 dagar från transaktionsdatumet. Därför är det av högsta vikt att du återkommer till oss så snart som möjligt så att vi kan hjälpa dig. Efter 120 dagar kan vi inte längre bestrida köpen.
Vänligen återkoppla till oss inom 7 dagar, annars kan vi behöva terminera ditt kort då vi misstänker att det hamnat i fel händer.

Med vänliga hälsningar,
Mynt`
}

const buildFortnoxNotificationText = ({ fraudCase, cardLastFourDigits }: NotificationTemplateParams) => {
  const txLines = formatTransactionLines(fraudCase)

  return `Hej,

Vi har observerat ett transaktionsförsök på ert Fortnox kort som slutar på nummer ${cardLastFourDigits} och undrar om det är köp som du själv har genomfört? Ditt kort är blockerat i dagsläget tills att vi fått tag i dig.

De transaktioner det gäller är följande:
${txLines}

Anledningen till att vi kontaktar dig är för att säkerställa att ditt kort inte har förlorats och att ingen obehörig har fått tag på dina kortuppgifter.
Om ni inte har utfört dessa köp vänligen besvara följande frågor:
· Har ni tidigare använt ert kort hos denna handlare?
· Har ni nyligen angett era kortuppgifter på någon okänd webbplats eller tjänst?

Om olovliga transaktioner har gjorts på kortet och behöver bestridas, är det viktigt att detta sker inom 120 dagar från transaktionsdatumet. Därför är det av högsta vikt att du återkommer till oss så snart som möjligt så att vi kan hjälpa dig. Efter 120 dagar kan vi inte längre bestrida köpen.

Vänligen återkoppla till oss inom 7 dagar, annars kan vi behöva terminera ditt kort då vi misstänker att det hamnat i fel händer.

Med vänliga hälsningar,
Mynt - Fortnox`
}

type EnglishTemplateConfig = {
  greeting: string
  cardName: string
  signOff: string
}

const buildEnglishNotificationText = (params: NotificationTemplateParams, config: EnglishTemplateConfig) => {
  const txLines = formatTransactionLines(params.fraudCase)

  return `${config.greeting}

We have observed a transaction attempt on your ${config.cardName} ending in number ${params.cardLastFourDigits} and are wondering if this is a purchase you made yourself? Your card is currently blocked until we have gotten in touch with you.

The transaction(s) in question are as follows:
${txLines}

The reason we are contacting you is to ensure that your card has not been lost and that no unauthorized person has obtained your card details. If you did not make these purchases, please answer the following questions:
· Have you previously used your card with this merchant?
· Have you recently entered your card details on any unknown website or service?

If unauthorized transactions have been made on the card and need to be disputed, it is important that this is done within 120 days of the transaction date. Therefore, it is of the utmost importance that you get back to us as soon as possible so we can assist you. After 120 days, we can no longer dispute the purchases.

Please get back to us within 7 days, otherwise we may need to terminate your card as we suspect it has fallen into the wrong hands.

Best regards,
${config.signOff}`
}

const buildVismaEconomicNotificationText = (params: NotificationTemplateParams) =>
  buildEnglishNotificationText(params, { greeting: 'Hello,', cardName: 'e-conomic card', signOff: 'Mynt - e-conomic' })

const buildDefaultEnglishNotificationText = (params: NotificationTemplateParams) =>
  buildEnglishNotificationText(params, {
    greeting: `Hello ${params.cardholderName},`,
    cardName: 'corporate card',
    signOff: 'Mynt',
  })

const notificationTemplateBuilders: Partial<Record<CardPartner, (params: NotificationTemplateParams) => string>> = {
  FORTNOX: buildFortnoxNotificationText,
  VISMAECONOMIC: buildVismaEconomicNotificationText,
}

export const buildNotificationText = (
  params: NotificationTemplateParams,
  cardPartner: CardPartner | undefined,
  country: string | undefined,
) => {
  const partnerBuilder = cardPartner && notificationTemplateBuilders[cardPartner]
  if (partnerBuilder) return partnerBuilder(params)
  return country === 'SE' || !country ? buildDefaultNotificationText(params) : buildDefaultEnglishNotificationText(params)
}
